import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Mic,
  MicOff,
  Music,
  MessageSquare,
  User,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
  Upload,
  FileAudio,
  Info,
  Headphones,
  Clock,
  Hash,
  Waves,
  Square,
  Play,
  Pause,
  Trash2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

// Phonetically-optimized prompts
const VOICE_MODEL_PROMPTS = [
  { id: 1, text: "The quick brown fox jumps over the lazy dog.", category: "pangram" },
  { id: 2, text: "Pack my box with five dozen liquor jugs.", category: "pangram" },
  { id: 3, text: "The cat sat on a flat black mat.", category: "vowels" },
  { id: 4, text: "Ten men went to fetch the red sled.", category: "vowels" },
  { id: 5, text: "Six quick chicks picked thick bricks.", category: "vowels" },
  { id: 6, text: "Bob's dog got lost in the fog.", category: "vowels" },
  { id: 7, text: "Jake baked eight cakes by the lake.", category: "vowels" },
  { id: 8, text: "She sees three green trees by the sea.", category: "vowels" },
  { id: 9, text: "I like to ride my bike at night.", category: "vowels" },
  { id: 10, text: "Joe knows the boat floats on the ocean.", category: "vowels" },
  { id: 11, text: "The boy enjoyed his noisy toy.", category: "diphthongs" },
  { id: 12, text: "The loud crowd shouted out proud sounds.", category: "diphthongs" },
  { id: 13, text: "She sells seashells by the seashore.", category: "clusters" },
  { id: 14, text: "Three thick threads through the throat.", category: "clusters" },
  { id: 15, text: "The bird chirped and turned in circles.", category: "r-colored" },
  { id: 16, text: "Hello, my name is... and I'm recording my voice today.", category: "natural" },
  { id: 17, text: "How are you doing? I hope you're having a great day.", category: "natural" },
  { id: 18, text: "That's really interesting. Tell me more about it.", category: "natural" },
  { id: 19, text: "Yes, absolutely! I completely agree with you.", category: "natural" },
  { id: 20, text: "One, two, three, four, five, six, seven, eight, nine, ten.", category: "numbers" },
];

const SONG_VOCAL_PROMPTS = [
  { id: 1, text: "La la la la la, la la la la la (sing on one note)", category: "warmup" },
  { id: 2, text: "Ah ah ah ah ah (open mouth, relaxed)", category: "warmup" },
  { id: 3, text: "Mee may mah moh moo (repeat 3 times)", category: "warmup" },
  { id: 4, text: "Sing a comfortable note and hold it for 5 seconds.", category: "sustain" },
  { id: 5, text: "Sing from low to high like a siren, then back down.", category: "range" },
  { id: 6, text: "Hum a melody you know well for 10 seconds.", category: "humming" },
  { id: 7, text: "Sing 'Happy Birthday' or any song you know.", category: "melody" },
  { id: 8, text: "Sing the same phrase loud, then soft.", category: "dynamics" },
];

const SPOKEN_CONTENT_PROMPTS = [
  { id: 1, text: "Welcome back to the show. Today we have something special.", category: "intro" },
  { id: 2, text: "Let me paint a picture for you. Imagine yourself there.", category: "narrative" },
  { id: 3, text: "The data shows a significant trend. Let me explain.", category: "educational" },
  { id: 4, text: "This brings us to an important question.", category: "engagement" },
  { id: 5, text: "Here's what I find most interesting about this.", category: "commentary" },
  { id: 6, text: "And that concludes today's episode. Thank you.", category: "outro" },
  { id: 7, text: "But wait, there's more to this story.", category: "transition" },
  { id: 8, text: "Now, I want you to really think about this.", category: "emphasis" },
];

const RecordingWizard = ({ onComplete, onBack }) => {
  const [step, setStep] = useState(1);
  const [intent, setIntent] = useState(null);
  const [showStudio, setShowStudio] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);

  // Recording studio state
  const [hasPermission, setHasPermission] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordings, setRecordings] = useState([]);
  const [currentPromptIndex, setCurrentPromptIndex] = useState(0);
  const [playingId, setPlayingId] = useState(null);

  // Refs
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const animationRef = useRef(null);
  const audioPlayerRef = useRef(null);

  const TOTAL_STEPS = 5;

  // Requirements based on intent
  const requirements = {
    'voice-model': {
      title: 'Voice Model',
      clipCount: { min: 20, ideal: 30 },
      clipDuration: { min: 5, ideal: 10 },
      totalDuration: { min: 180, ideal: 300 },
      prompts: VOICE_MODEL_PROMPTS
    },
    'song-vocals': {
      title: 'Song / Vocals',
      clipCount: { min: 6, ideal: 10 },
      clipDuration: { min: 10, ideal: 20 },
      totalDuration: { min: 120, ideal: 240 },
      prompts: SONG_VOCAL_PROMPTS
    },
    'spoken-content': {
      title: 'Spoken Content',
      clipCount: { min: 15, ideal: 25 },
      clipDuration: { min: 10, ideal: 15 },
      totalDuration: { min: 180, ideal: 360 },
      prompts: SPOKEN_CONTENT_PROMPTS
    }
  };

  const currentReqs = intent ? requirements[intent] : null;
  const prompts = currentReqs?.prompts || [];
  const currentPrompt = prompts[currentPromptIndex];

  // Calculate progress
  const totalDuration = recordings.reduce((sum, r) => sum + r.duration, 0);
  const minDuration = currentReqs?.totalDuration?.min || 180;
  const minClips = currentReqs?.clipCount?.min || 20;
  const progressPercent = Math.min(100, (totalDuration / minDuration) * 100);
  const isRecordingComplete = recordings.length >= minClips && totalDuration >= minDuration;

  // Request microphone permission
  const requestPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 44100 } 
      });
      streamRef.current = stream;
      setHasPermission(true);
      
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      analyserRef.current.fftSize = 256;
      
      monitorAudioLevel();
    } catch (err) {
      console.error('Microphone permission denied:', err);
      setHasPermission(false);
    }
  };

  const monitorAudioLevel = useCallback(() => {
    if (!analyserRef.current) return;
    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    const updateLevel = () => {
      if (!analyserRef.current) return;
      analyserRef.current.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      setAudioLevel((average / 255) * 100);
      animationRef.current = requestAnimationFrame(updateLevel);
    };
    updateLevel();
  }, []);

  const startRecording = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const mediaRecorder = new MediaRecorder(streamRef.current, { mimeType: 'audio/webm;codecs=opus' });
    
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    
    mediaRecorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
      const url = URL.createObjectURL(blob);
      const newRecording = {
        id: Date.now(),
        promptId: currentPrompt?.id,
        promptText: currentPrompt?.text || 'Free recording',
        blob, url,
        duration: recordingTime,
        timestamp: new Date().toISOString()
      };
      setRecordings(prev => [...prev, newRecording]);
      setRecordingTime(0);
      // Auto advance to next prompt
      if (currentPromptIndex < prompts.length - 1) {
        setCurrentPromptIndex(prev => prev + 1);
      }
    };
    
    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start(100);
    setIsRecording(true);
    timerRef.current = setInterval(() => setRecordingTime(prev => prev + 0.1), 100);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const playRecording = (recording) => {
    if (audioPlayerRef.current) audioPlayerRef.current.pause();
    if (playingId === recording.id) { setPlayingId(null); return; }
    const audio = new Audio(recording.url);
    audioPlayerRef.current = audio;
    audio.onended = () => setPlayingId(null);
    audio.play();
    setPlayingId(recording.id);
  };

  const deleteRecording = (id) => {
    setRecordings(prev => prev.filter(r => r.id !== id));
    if (playingId === id) { audioPlayerRef.current?.pause(); setPlayingId(null); }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const tenths = Math.floor((seconds % 1) * 10);
    if (mins > 0) return `${mins}:${secs.toString().padStart(2, '0')}`;
    return `${secs}.${tenths}s`;
  };

  const getLevelColor = () => {
    if (audioLevel < 20) return 'bg-zinc-600';
    if (audioLevel < 60) return 'bg-emerald-500';
    if (audioLevel < 85) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const getLevelStatus = () => {
    if (audioLevel < 20) return { text: 'Speak louder', color: 'text-zinc-400' };
    if (audioLevel < 60) return { text: 'Good ✓', color: 'text-emerald-400' };
    if (audioLevel < 85) return { text: 'Loud', color: 'text-amber-400' };
    return { text: 'Too loud!', color: 'text-red-400' };
  };

  // File upload handler
  const getAudioDuration = (file) => {
    return new Promise((resolve) => {
      const audio = new Audio();
      audio.addEventListener('loadedmetadata', () => resolve(audio.duration));
      audio.addEventListener('error', () => resolve(file.size / 16000));
      audio.src = URL.createObjectURL(file);
    });
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    const processed = await Promise.all(files.map(async (f) => ({
      name: f.name, size: f.size, type: f.type,
      duration: await getAudioDuration(f), file: f
    })));
    const allFiles = [...uploadedFiles, ...processed];
    setUploadedFiles(allFiles);
    validateFiles(allFiles);
  };

  const validateFiles = (files) => {
    if (!currentReqs) return;
    const errors = [];
    const totalDur = files.reduce((sum, f) => sum + f.duration, 0);
    if (files.length < currentReqs.clipCount.min) {
      errors.push(`Need ${currentReqs.clipCount.min}+ clips (have ${files.length})`);
    }
    if (totalDur < currentReqs.totalDuration.min) {
      errors.push(`Need ${Math.round(currentReqs.totalDuration.min/60)}+ min (have ${formatTime(totalDur)})`);
    }
    setValidationErrors(errors);
  };

  const removeFile = (index) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(newFiles);
    validateFiles(newFiles);
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(track => track.stop());
      if (audioContextRef.current) audioContextRef.current.close();
    };
  }, []);

  // Open studio
  const openStudio = () => {
    setShowStudio(true);
    requestPermission();
  };

  const handleComplete = () => {
    const allRecordings = [
      ...recordings.map(r => ({ name: `recording_${r.id}.webm`, duration: r.duration, blob: r.blob, url: r.url })),
      ...uploadedFiles
    ];
    onComplete({ intent, files: allRecordings, requirements: currentReqs });
  };

  const canProceed = () => {
    if (step === 1) return intent !== null;
    if (step === 4) {
      const allCount = recordings.length + uploadedFiles.length;
      const allDuration = totalDuration + uploadedFiles.reduce((sum, f) => sum + f.duration, 0);
      return allCount >= (currentReqs?.clipCount.min || 0) && allDuration >= (currentReqs?.totalDuration.min || 0);
    }
    return true;
  };

  // ============ RECORDING STUDIO VIEW ============
  if (showStudio) {
    if (hasPermission === false) {
      return (
        <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center p-4">
          <Card className="max-w-md bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-6 text-center">
              <MicOff className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-zinc-100 mb-2">Microphone Access Required</h2>
              <p className="text-zinc-400 mb-4">Allow microphone access in your browser to record.</p>
              <div className="flex gap-3 justify-center">
                <Button variant="outline" onClick={() => setShowStudio(false)} className="border-zinc-700">Go Back</Button>
                <Button onClick={requestPermission}>Try Again</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    if (hasPermission === null) {
      return (
        <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center">
          <div className="text-center">
            <Mic className="w-12 h-12 text-zinc-400 mx-auto mb-4 animate-pulse" />
            <p className="text-zinc-400">Requesting microphone access...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-[#0a0a0b]" data-testid="recording-studio">
        <header className="border-b border-zinc-800 bg-zinc-900/50 sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => setShowStudio(false)} className="text-zinc-400">
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
            <h1 className="text-sm font-medium text-zinc-100">Recording Studio</h1>
            <Button 
              size="sm" 
              onClick={() => { setShowStudio(false); setStep(4); }}
              disabled={recordings.length === 0}
              className={recordings.length > 0 ? 'bg-emerald-600' : 'bg-zinc-700'}
            >
              Done ({recordings.length})
            </Button>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 py-6">
          {/* Progress */}
          <Card className="bg-zinc-900/50 border-zinc-800 mb-6">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-zinc-400">Progress</span>
                <span className="text-sm text-zinc-300">
                  {recordings.length}/{minClips} clips • {formatTime(totalDuration)}/{Math.floor(minDuration/60)}:00
                </span>
              </div>
              <Progress value={progressPercent} className="h-2 bg-zinc-800" />
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recording Panel */}
            <div className="space-y-4">
              {/* Current Prompt */}
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm text-zinc-400">
                      Prompt {currentPromptIndex + 1} of {prompts.length}
                    </CardTitle>
                    <Badge variant="outline" className="border-zinc-700 text-zinc-400">
                      {currentPrompt?.category}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-lg text-zinc-100 leading-relaxed mb-4">"{currentPrompt?.text}"</p>
                  <div className="flex justify-between">
                    <Button variant="ghost" size="sm" onClick={() => setCurrentPromptIndex(Math.max(0, currentPromptIndex - 1))} disabled={currentPromptIndex === 0} className="text-zinc-400">
                      <ChevronLeft className="w-4 h-4 mr-1" />Prev
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setCurrentPromptIndex(Math.min(prompts.length - 1, currentPromptIndex + 1))} disabled={currentPromptIndex === prompts.length - 1} className="text-zinc-400">
                      Next<ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Recording Controls */}
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-6">
                  {/* Level Meter */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-zinc-500">Level</span>
                      <span className={`text-xs ${getLevelStatus().color}`}>{getLevelStatus().text}</span>
                    </div>
                    <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
                      <div className={`h-full transition-all duration-75 ${getLevelColor()}`} style={{ width: `${audioLevel}%` }} />
                    </div>
                  </div>

                  {/* Timer */}
                  <div className="text-center mb-6">
                    <div className={`text-4xl font-mono ${isRecording ? 'text-red-400' : 'text-zinc-300'}`}>
                      {formatTime(recordingTime)}
                    </div>
                    {isRecording && (
                      <div className="flex items-center justify-center gap-2 mt-2">
                        <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                        <span className="text-sm text-red-400">Recording</span>
                      </div>
                    )}
                  </div>

                  {/* Record Button */}
                  <div className="flex justify-center">
                    {!isRecording ? (
                      <Button size="lg" onClick={startRecording} className="w-20 h-20 rounded-full bg-red-600 hover:bg-red-700" data-testid="record-btn">
                        <Mic className="w-8 h-8" />
                      </Button>
                    ) : (
                      <Button size="lg" onClick={stopRecording} className="w-20 h-20 rounded-full bg-zinc-700 hover:bg-zinc-600" data-testid="stop-btn">
                        <Square className="w-8 h-8" />
                      </Button>
                    )}
                  </div>
                  <p className="text-center text-xs text-zinc-500 mt-4">
                    {isRecording ? 'Click to stop' : 'Click to record'}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Recordings List */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-zinc-100 flex items-center justify-between">
                  <span>Recordings</span>
                  <Badge className={recordings.length >= minClips ? 'bg-emerald-600' : 'bg-zinc-700'}>{recordings.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {recordings.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500">
                    <Mic className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>No recordings yet</p>
                  </div>
                ) : (
                  <ScrollArea className="h-[350px]">
                    <div className="space-y-2">
                      {recordings.map((rec) => (
                        <div key={rec.id} className="flex items-center gap-2 p-2 rounded bg-zinc-800/50 border border-zinc-700 group">
                          <Button variant="ghost" size="sm" onClick={() => playRecording(rec)} className="w-8 h-8 p-0">
                            {playingId === rec.id ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                          </Button>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-zinc-300 truncate">{rec.promptText.slice(0, 30)}...</p>
                            <p className="text-xs text-zinc-500">{formatTime(rec.duration)}</p>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => deleteRecording(rec.id)} className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-red-400">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  // ============ MAIN WIZARD VIEW ============
  return (
    <div className="min-h-screen bg-[#0a0a0b] py-8 px-4" data-testid="recording-wizard">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-semibold text-zinc-100">Recording Setup</h1>
          <p className="text-zinc-500 mt-1">Let's get your voice recordings ready</p>
        </div>

        <div className="mb-8">
          <div className="flex justify-between text-xs text-zinc-500 mb-2">
            <span>Step {step} of {TOTAL_STEPS}</span>
            <span>{Math.round((step / TOTAL_STEPS) * 100)}%</span>
          </div>
          <Progress value={(step / TOTAL_STEPS) * 100} className="h-1.5 bg-zinc-800" />
        </div>

        {/* Step 1: Intent */}
        {step === 1 && (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-zinc-100 flex items-center gap-2">
                <Info className="w-5 h-5 text-zinc-400" />What are you trying to make?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <RadioGroup value={intent} onValueChange={setIntent} className="space-y-3">
                {[
                  { value: 'voice-model', icon: User, title: 'Voice Model', desc: 'Train an AI to speak in your voice' },
                  { value: 'song-vocals', icon: Music, title: 'Song / Vocals', desc: 'For singing and music' },
                  { value: 'spoken-content', icon: MessageSquare, title: 'Spoken Content', desc: 'Podcasts, narration' }
                ].map(opt => (
                  <div key={opt.value} className={`flex items-start gap-4 p-4 rounded-lg border cursor-pointer ${intent === opt.value ? 'bg-zinc-800/50 border-zinc-600' : 'border-zinc-800 hover:border-zinc-700'}`} onClick={() => setIntent(opt.value)} data-testid={`intent-${opt.value}`}>
                    <RadioGroupItem value={opt.value} id={opt.value} className="mt-1" />
                    <div>
                      <Label htmlFor={opt.value} className="text-zinc-100 font-medium cursor-pointer">
                        <opt.icon className="w-4 h-4 inline mr-2" />{opt.title}
                      </Label>
                      <p className="text-sm text-zinc-500 mt-1">{opt.desc}</p>
                    </div>
                  </div>
                ))}
              </RadioGroup>
            </CardContent>
          </Card>
        )}

        {/* Step 2: What to Record */}
        {step === 2 && currentReqs && (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-zinc-100 flex items-center gap-2">
                <Mic className="w-5 h-5 text-zinc-400" />What to Record
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                  <Hash className="w-5 h-5 text-zinc-400 mx-auto mb-2" />
                  <div className="text-xl font-semibold text-zinc-100">{currentReqs.clipCount.min}+</div>
                  <div className="text-xs text-zinc-500">clips</div>
                </div>
                <div className="text-center p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                  <Clock className="w-5 h-5 text-zinc-400 mx-auto mb-2" />
                  <div className="text-xl font-semibold text-zinc-100">{currentReqs.clipDuration.min}-{currentReqs.clipDuration.ideal}s</div>
                  <div className="text-xs text-zinc-500">each</div>
                </div>
                <div className="text-center p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                  <Waves className="w-5 h-5 text-zinc-400 mx-auto mb-2" />
                  <div className="text-xl font-semibold text-zinc-100">{Math.round(currentReqs.totalDuration.min / 60)}+ min</div>
                  <div className="text-xs text-zinc-500">total</div>
                </div>
              </div>
              <Alert className="bg-zinc-800/50 border-zinc-700">
                <Info className="w-4 h-4" />
                <AlertDescription className="text-zinc-300">
                  We'll show you exactly what to say. Just read the prompts naturally.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        )}

        {/* Step 3: How to Record */}
        {step === 3 && (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-zinc-100 flex items-center gap-2">
                <Headphones className="w-5 h-5 text-zinc-400" />How to Record
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                {['Quiet room - no background noise', '6-12 inches from mic', 'Speak clearly and naturally'].map((tip, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded bg-zinc-800/50 border border-zinc-700">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    <span className="text-zinc-200">{tip}</span>
                  </div>
                ))}
              </div>

              {/* Two options */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                <button
                  onClick={openStudio}
                  className="p-4 rounded-lg bg-emerald-950/30 border-2 border-emerald-700 text-left hover:bg-emerald-950/50 transition-colors"
                  data-testid="open-studio-btn"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center">
                      <Mic className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-emerald-100">Record in App</div>
                      <div className="text-xs text-emerald-400">Recommended</div>
                    </div>
                  </div>
                  <p className="text-sm text-emerald-200/70">Guided prompts + level meter</p>
                </button>

                <button
                  onClick={() => setStep(4)}
                  className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700 text-left hover:bg-zinc-800 transition-colors"
                  data-testid="skip-to-upload-btn"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center">
                      <Upload className="w-5 h-5 text-zinc-300" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-zinc-100">Upload Files</div>
                      <div className="text-xs text-zinc-500">Already recorded</div>
                    </div>
                  </div>
                  <p className="text-sm text-zinc-400">Upload existing audio files</p>
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Upload/Review */}
        {step === 4 && currentReqs && (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-zinc-100 flex items-center gap-2">
                <Upload className="w-5 h-5 text-zinc-400" />Your Recordings
              </CardTitle>
              <CardDescription className="text-zinc-500">
                {recordings.length > 0 ? `${recordings.length} recorded in app` : ''} 
                {recordings.length > 0 && uploadedFiles.length > 0 ? ' + ' : ''}
                {uploadedFiles.length > 0 ? `${uploadedFiles.length} uploaded` : ''}
                {recordings.length === 0 && uploadedFiles.length === 0 ? 'Add your recordings' : ''}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Recordings from studio */}
              {recordings.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm text-zinc-400">Recorded in app ({recordings.length})</h4>
                  {recordings.slice(0, 3).map((rec) => (
                    <div key={rec.id} className="flex items-center gap-2 p-2 rounded bg-emerald-950/30 border border-emerald-800">
                      <FileAudio className="w-4 h-4 text-emerald-500" />
                      <span className="text-sm text-zinc-300 truncate flex-1">{rec.promptText.slice(0, 30)}...</span>
                      <span className="text-xs text-zinc-500">{formatTime(rec.duration)}</span>
                    </div>
                  ))}
                  {recordings.length > 3 && <p className="text-xs text-zinc-500">+{recordings.length - 3} more</p>}
                </div>
              )}

              {/* Upload area */}
              <label className="block">
                <div className="border-2 border-dashed border-zinc-700 rounded-lg p-6 text-center cursor-pointer hover:border-zinc-600">
                  <Upload className="w-6 h-6 text-zinc-600 mx-auto mb-2" />
                  <p className="text-zinc-400 text-sm">Drop files or click to upload</p>
                  <p className="text-xs text-zinc-500">WAV, MP3, M4A</p>
                  <input type="file" className="hidden" multiple accept="audio/*" onChange={handleFileUpload} />
                </div>
              </label>

              {/* Uploaded files */}
              {uploadedFiles.length > 0 && (
                <div className="space-y-2">
                  {uploadedFiles.map((file, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded bg-zinc-800/50 border border-zinc-700">
                      <div className="flex items-center gap-2">
                        <FileAudio className="w-4 h-4 text-zinc-500" />
                        <span className="text-sm text-zinc-300 truncate max-w-[200px]">{file.name}</span>
                        <span className="text-xs text-zinc-500">{formatTime(file.duration)}</span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeFile(i)} className="text-zinc-500 hover:text-red-400">×</Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Validation */}
              {!canProceed() && (
                <Alert className="bg-amber-950/30 border-amber-800">
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                  <AlertDescription className="text-amber-200">
                    Need {currentReqs.clipCount.min}+ clips and {Math.round(currentReqs.totalDuration.min/60)}+ minutes total
                  </AlertDescription>
                </Alert>
              )}

              {canProceed() && (
                <Alert className="bg-emerald-950/30 border-emerald-800">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  <AlertDescription className="text-emerald-200">Ready to continue!</AlertDescription>
                </Alert>
              )}

              {/* Record more button */}
              <Button variant="outline" onClick={openStudio} className="w-full border-zinc-700">
                <Mic className="w-4 h-4 mr-2" />Record More
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 5: What Happens Next */}
        {step === 5 && (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-zinc-100 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />Ready!
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert className="bg-zinc-800/50 border-zinc-700">
                <Info className="w-4 h-4" />
                <AlertDescription className="text-zinc-200">
                  We'll package your recordings into a notebook for Google Colab. Training takes 30-60 minutes on a free GPU.
                </AlertDescription>
              </Alert>
              <div className="text-sm text-zinc-400">
                <p>Total: {recordings.length + uploadedFiles.length} clips, {formatTime(totalDuration + uploadedFiles.reduce((s,f) => s + f.duration, 0))}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button variant="ghost" onClick={() => step === 1 ? onBack() : setStep(step - 1)} className="text-zinc-400">
            <ArrowLeft className="w-4 h-4 mr-2" />{step === 1 ? 'Cancel' : 'Back'}
          </Button>
          {step < TOTAL_STEPS ? (
            <Button onClick={() => setStep(step + 1)} disabled={!canProceed()} className="bg-zinc-100 text-zinc-900 hover:bg-zinc-200" data-testid="wizard-next-btn">
              Continue<ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleComplete} className="bg-emerald-600 hover:bg-emerald-700" data-testid="wizard-complete-btn">
              <CheckCircle2 className="w-4 h-4 mr-2" />Start Training Setup
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecordingWizard;
